import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { IconButton, Modal } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { Typography } from '@vibTheme/components/_base/Typography';
import { Button } from '@vibTheme/components/_base/Button';
import VibDataGrid, {
  VibDataGridColDef,
  VibDataGridProps,
} from '@vibTheme/components/VibDataGrid';
import { InputText } from './InputText';
import { InputSwitch } from './InputSwitch';
import { useFirstRender } from '@hooks/useFirstRender';
import {
  filterCommunitiesByStatus,
  formatCommunitiesSelectionLabel,
  mergeVisibleSelection,
} from '@utils/communitiesStatusFilter';

export type SelectableCommunity = {
  id: number;
  name: string;
  active: boolean;
  /** A função do usuário na comunidade, já traduzida para exibição. */
  userRoleLabel: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** Tudo que o campo oferece, ativas e inativas. */
  communities: SelectableCommunity[];
  /** A seleção já confirmada, de onde cada abertura recomeça. */
  selectedIds: number[];
  onSubmit: (ids: number[]) => void;
  noRowsMessage?: string;
  /**
   * Para as telas que já restringem o campo às ativas por conta própria - não há status para mostrar
   * nem o que filtrar, e o interruptor só prometeria uma lista maior que a tela não aceita.
   */
  hideStatusFilter?: boolean;
};

/**
 * A lista de comunidades do campo "Comunidade", compartilhada pelo seletor da Dash e pelo dos relatórios.
 *
 * Os dois seletores desenhavam a mesma modal em dois arquivos, e é aqui que ela existe uma vez só - o
 * filtro de status precisa se comportar igual nos dois, e duas cópias divergiriam no primeiro ajuste.
 *
 * A seleção pendente mora aqui: marcar linhas não decide nada até o "Adicionar", e o "Cancelar" (como o
 * X e o clique fora) devolve a modal ao que estava confirmado em vez de guardar meias-escolhas para a
 * próxima abertura.
 */
export const CommunitiesSelectionModal = ({
  isOpen,
  onClose,
  communities,
  selectedIds,
  onSubmit,
  noRowsMessage,
  hideStatusFilter,
}: Props) => {
  const { control, watch, setValue } = useForm({
    defaultValues: { search: '', onlyActive: true },
  });
  const search = watch('search');
  const onlyActive = watch('onlyActive');

  const [pendingIds, setPendingIds] = useState<number[]>(selectedIds);

  /**
   * Numa lista sem nenhuma ativa não há nada a separar, e o filtro só esconderia tudo - o mesmo motivo do
   * fallback de `resolveDefaultCommunityIds`: a tela não pode abrir vazia para quem não tem outra opção.
   * Então o interruptor sai de cena, como já sai quando a tela só aceita ativas.
   *
   * Sair de cena, e não ser desligado: a lista muda debaixo desta modal, que o ConversionFilters mantém
   * montada quando o produtor troca de função. Desligar o interruptor gravaria a escolha automática por
   * cima da escolha dele, e a função seguinte - com ativas de novo - abriria mostrando as inativas sem
   * ninguém ter pedido.
   */
  const hasActiveCommunity = communities.some((community) => community.active);
  const offersStatusFilter = !hideStatusFilter && hasActiveCommunity;

  /**
   * Cada abertura parte da seleção confirmada e da busca limpa. O interruptor não é reposto de
   * propósito: quem acabou de desligá-lo para achar uma comunidade inativa continua olhando a lista
   * inteira ao reabrir.
   */
  useEffect(() => {
    if (!isOpen) return;

    setPendingIds(
      selectedIds.filter((id) =>
        communities.some((community) => community.id === id),
      ),
    );
    setValue('search', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const filtersStatus = offersStatusFilter && onlyActive;
  const scope = filterCommunitiesByStatus(communities, filtersStatus);
  const rows = search
    ? scope.filter((community) =>
        community.name?.toLowerCase().includes(search.toLowerCase()),
      )
    : scope;

  /**
   * Mexer no interruptor desmarca quem saiu da lista: o que o produtor não vê mais não continua sendo
   * medido às escondidas. Ligar o filtro e ficar com uma inativa selecionada seria pedir para ele
   * confiar no rodapé para saber o que a tela está contando.
   *
   * Só a mudança do interruptor desmarca, e não a busca - a lista se estreita a cada letra digitada, e
   * perder a seleção por causa disso tornaria o campo inutilizável. Também não desmarca na abertura: a
   * seleção guardada é a escolha do produtor, inativas incluídas, e é o rodapé que a declara.
   */
  const isFirstRender = useFirstRender();
  useEffect(() => {
    if (isFirstRender) return;

    const scopeIds = scope.map((community) => community.id);
    setPendingIds((current) => current.filter((id) => scopeIds.includes(id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersStatus]);

  const selectedInScope = scope.filter((community) =>
    pendingIds.includes(community.id),
  ).length;
  const hiddenSelected = pendingIds.length - selectedInScope;

  const onSelectionModelChange: VibDataGridProps['onSelectionModelChange'] = (
    ids,
  ) =>
    setPendingIds(
      mergeVisibleSelection(
        pendingIds,
        ids as number[],
        rows.map((community) => community.id),
      ),
    );

  return (
    <Modal open={isOpen} onClose={onClose} className="flex p-main">
      <div className="flex flex-col bg-white rounded-default p-main mx-auto w-full sm:w-[640px]">
        <div className="relative">
          <Typography component="h1">Comunidades</Typography>
          <IconButton className="absolute top-0 right-0 p-0" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>

        <div className="flex flex-col items-start gap-sm my-main sm:flex-row sm:items-center sm:gap-main">
          <InputText
            type="search"
            name="search"
            control={control}
            placeholder="Buscar"
            InputProps={{
              endAdornment: <SearchIcon className="text-[#6b7280]" />,
            }}
            className="w-full sm:grow"
          />
          {offersStatusFilter && (
            // O `shrink-0` fica no invólucro, e não no InputSwitch: o className dele desce para o
            // Switch, e sem isso a busca comprime o rótulo até ele quebrar em duas linhas.
            <div className="shrink-0">
              <InputSwitch
                name="onlyActive"
                label="Apenas ativas"
                control={control}
              />
            </div>
          )}
        </div>

        <div className="grow [&_.MuiGrid-root]:h-full">
          <VibDataGrid
            // A coluna de status fica mesmo quando o interruptor sai: numa lista toda inativa, ela é o
            // que explica por que não há o que filtrar.
            columns={hideStatusFilter ? columns : columnsWithStatus}
            rows={parseRows(rows)}
            checkboxSelection
            onSelectionModelChange={onSelectionModelChange}
            selectionModel={pendingIds}
            disableSelectionOnClick={false}
            autoHeight={false}
            className="h-full"
            keepNonExistentRowsSelected
            // Quem conta a seleção é o rodapé da modal, que diz de qual escopo fala - o "N linhas
            // selecionadas" da grid repetiria o número contando só o que o filtro deixou à vista.
            hideFooterSelectedRowCount
            noRowsMessage={noRowsMessage}
          />
        </div>

        <div className="flex flex-col gap-sm mt-main sm:flex-row sm:items-center sm:justify-between">
          <Typography className="text-sm text-lightBlack">
            {formatCommunitiesSelectionLabel(
              selectedInScope,
              scope.length,
              filtersStatus,
              hiddenSelected,
            )}
          </Typography>
          <div className="flex gap-sm">
            <Button variant="outlined" className="grow" onClick={onClose}>
              Cancelar
            </Button>
            <Button className="grow" onClick={() => onSubmit(pendingIds)}>
              Adicionar
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const ACTIVE_LABEL = 'Ativa';
const INACTIVE_LABEL = 'Inativa';

const columns: VibDataGridColDef[] = [
  { field: 'name', headerName: 'Nome' },
  { field: 'userRoleLabel', headerName: 'Função de usuário' },
];

/**
 * A bolinha de status vem de um `::before`, e não de um `renderCell`.
 *
 * O VibDataGrid mede a largura das colunas percorrendo os `.MuiDataGrid-cellContent` da grid por índice,
 * assumindo um por coluna em cada linha (`idx + i * columns.length`). Uma célula com `renderCell` não
 * produz esse nó: a contagem sai de fase e as larguras passam a ser aplicadas nas colunas erradas - a
 * coluna do status levaria a medida do nome. O pseudo-elemento pinta a bolinha sem mexer no DOM da
 * célula, então a conta continua batendo.
 */
const statusCellStyles =
  "flex items-center before:content-[''] before:mr-1.5 before:h-2 before:w-2 before:shrink-0 before:rounded-full";

const columnsWithStatus: VibDataGridColDef[] = [
  ...columns,
  {
    field: 'status',
    headerName: 'Status',
    cellClassName: ({ value }) =>
      value === ACTIVE_LABEL
        ? `${statusCellStyles} text-success before:bg-success`
        : `${statusCellStyles} text-silver before:bg-silver`,
  },
];

const parseRows = (communities: SelectableCommunity[]) =>
  communities.map((community) => ({
    id: community.id,
    name: community.name,
    userRoleLabel: community.userRoleLabel,
    status: community.active ? ACTIVE_LABEL : INACTIVE_LABEL,
  }));
